import { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toPng } from 'html-to-image'

// ── Types ────────────────────────────────────────────────────────────────────

type Recommendation = {
  title: string
  reason: string
  label?: string | null
  image?: string | null
  rank?: number
}

type ShareData = {
  details: {
    consideringGame?: string
    intentWhy?: string | null
    target?: { title?: string; image?: string }
    recommendations?: Recommendation[]
  }
  requested_at: string
}

// ── Palette ──────────────────────────────────────────────────────────────────

const C = {
  pageBg:   '#3d2a1e',
  card:     '#fdf8f2',
  terracotta: '#c4714a',
  darkRoast: '#3d2a1e',
  bark:     '#6a5040',
  driftwood: '#b09080',
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const upgradeIgdbUrl = (url?: string | null) => {
  if (!url) return url
  return url
    .replace(/\/t_thumb\//, '/t_cover_big_2x/')
    .replace(/\/t_cover_big(?!_2x)\//, '/t_cover_big_2x/')
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

// ── Cover card ───────────────────────────────────────────────────────────────

const GameCard = ({ rec, idx }: { rec: Recommendation; idx: number }) => {
  const img = upgradeIgdbUrl(rec.image)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
      <div style={{
        position: 'relative', width: '100%', aspectRatio: '2/3',
        borderRadius: '10px', overflow: 'hidden',
        background: `hsl(${(idx * 53 + 15) % 360}, 20%, 30%)`,
        boxShadow: '0 2px 12px rgba(61,42,30,0.18)',
      }}>
        {img ? (
          <>
            <img src={img} alt="" aria-hidden crossOrigin="anonymous" style={{
              position: 'absolute', inset: '-10%', width: '120%', height: '120%',
              objectFit: 'cover', filter: 'blur(10px) brightness(0.45)',
            }} />
            <img src={img} alt={rec.title} crossOrigin="anonymous" style={{
              position: 'relative', width: '100%', height: '100%',
              objectFit: 'contain', display: 'block',
            }} />
          </>
        ) : null}
      </div>
      <div style={{ padding: '0 0.1rem' }}>
        <p style={{ fontSize: '0.72rem', fontWeight: 700, color: C.bark, lineHeight: 1.3, margin: '0 0 0.2rem' }}>
          {rec.title}
        </p>
      </div>
    </div>
  )
}

// ── Share drawer ─────────────────────────────────────────────────────────────

const IconWhatsApp = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
)
const IconReddit = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
)
const IconX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
)
const IconBluesky = () => (
  <svg width="20" height="20" viewBox="0 0 600 530" fill="currentColor">
    <path d="m135.72 44.03c66.496 49.921 138.02 151.14 164.28 205.46 26.262-54.316 97.782-155.54 164.28-205.46 47.98-36.021 125.72-63.892 125.72 24.795 0 17.712-10.155 148.79-16.111 170.07-20.703 73.984-96.144 92.854-163.25 81.433 117.3 19.964 147.14 86.092 82.697 152.22-122.39 125.59-175.91-31.511-189.63-71.766-2.514-7.3797-3.6904-10.832-3.7077-7.8964-0.0174-2.9357-1.1937 0.51669-3.7077 7.8964-13.714 40.255-67.233 197.36-189.63 71.766-64.444-66.128-34.605-132.26 82.697-152.22-67.109 11.421-142.55-7.4491-163.25-81.433-5.9562-21.282-16.111-152.36-16.111-170.07 0-88.687 77.742-60.816 125.72-24.795z"/>
  </svg>
)
const IconCopy = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
)
const IconImg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
  </svg>
)

type DrawerProps = { open: boolean; onClose: () => void; url: string; title: string; cardRef: React.RefObject<HTMLDivElement> }

const ShareDrawer = ({ open, onClose, url, title, cardRef }: DrawerProps) => {
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)
  if (!open) return null

  const enc = encodeURIComponent
  const handleCopy = () => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => { setCopied(false); onClose() }, 1200) }
  const handleSaveImage = async () => {
    if (!cardRef.current) return
    setSaving(true)
    try {
      const imgs = Array.from(cardRef.current.querySelectorAll('img'))
      const origSrcs = imgs.map(img => img.src)
      await Promise.all(imgs.map(async (img) => {
        if (!img.src || img.src.startsWith('data:')) return
        try {
          const blob = await fetch(img.src).then(r => r.blob())
          img.src = await blobToDataUrl(blob)
          if (!img.complete) await new Promise(r => { img.onload = r })
        } catch { /* leave as-is */ }
      }))
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2 })
      imgs.forEach((img, i) => { img.src = origSrcs[i] })
      const a = document.createElement('a')
      a.download = 'backlog-buddy-picks.png'
      a.href = dataUrl
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (e) { console.error('Save as image failed:', e) }
    setSaving(false)
    onClose()
  }

  const items = [
    { label: 'WhatsApp', icon: <IconWhatsApp />, fn: () => { window.open(`https://wa.me/?text=${enc(title + ' ' + url)}`, '_blank'); onClose() } },
    { label: 'Reddit', icon: <IconReddit />, fn: () => { window.open(`https://reddit.com/submit?url=${enc(url)}&title=${enc(title)}`, '_blank'); onClose() } },
    { label: 'X (Twitter)', icon: <IconX />, fn: () => { window.open(`https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`, '_blank'); onClose() } },
    { label: 'Bluesky', icon: <IconBluesky />, fn: () => { window.open(`https://bsky.app/intent/compose?text=${enc(title + ' ' + url)}`, '_blank'); onClose() } },
    { label: copied ? 'Copied!' : 'Copy link', icon: <IconCopy />, fn: handleCopy },
    { label: saving ? 'Saving…' : 'Save as image', icon: <IconImg />, fn: handleSaveImage },
  ]

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 20, backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 21, background: '#fff', borderRadius: '20px 20px 0 0', padding: '0.5rem 0 2rem', boxShadow: '0 -4px 40px rgba(0,0,0,0.25)', fontFamily: 'Nunito, sans-serif' }}>
        <div style={{ width: '40px', height: '4px', background: '#e0d6cc', borderRadius: '99px', margin: '0.75rem auto 1rem' }} />
        {items.map(item => (
          <button key={item.label} onClick={item.fn} style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', padding: '0.85rem 1.5rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: 600, color: C.darkRoast, fontFamily: 'Nunito, sans-serif', textAlign: 'left' }}>
            <span style={{ color: C.darkRoast, opacity: 0.55, display: 'flex' }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>
    </>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

const SharePage = () => {
  const { shareId } = useParams<{ shareId: string }>()
  const [data, setData] = useState<ShareData | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!shareId) return
    const load = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/shares/${shareId}`)
        if (!res.ok) { setNotFound(true); return }
        setData(await res.json())
      } catch { setNotFound(true) }
      finally { setLoading(false) }
    }
    load()
  }, [shareId])

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="loading loading-spinner loading-lg" style={{ color: C.terracotta }} />
    </div>
  )

  if (notFound || !data) return (
    <div style={{ minHeight: '100vh', background: C.pageBg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '2rem' }}>
      <p style={{ color: '#fdf8f2', fontSize: '1.125rem' }}>This share link has expired or doesn't exist.</p>
      <Link to="/" style={{ color: C.terracotta, fontWeight: 600 }}>← Back to Backlog Explorer</Link>
    </div>
  )

  const { details } = data
  const targetTitle = details.target?.title || details.consideringGame || 'a game'
  const intentWhy = details.intentWhy || null
  const coverImage = upgradeIgdbUrl(details.target?.image)
  const recs = details.recommendations || []
  const shareUrl = window.location.href
  const shareTitle = `Found these hiding in my backlog 🙈`

  return (
    <div style={{ minHeight: '100vh', background: C.pageBg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2.5rem 1rem 5rem', fontFamily: 'Nunito, sans-serif' }}>

      {/* ── Card ── */}
      <div ref={cardRef} style={{ width: '100%', maxWidth: '480px', background: C.card, borderRadius: '24px', boxShadow: '0 28px 64px rgba(0,0,0,0.5)', overflow: 'hidden', color: C.darkRoast }}>

        {/* Header */}
        <div style={{ padding: '1.5rem 1.5rem 1.25rem' }}>
          <p style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.16em', fontWeight: 800, color: C.terracotta, marginBottom: '1.25rem', textAlign: 'center' }}>
            ✦ Backlog Buddy
          </p>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            {coverImage && (
              <div style={{ position: 'relative', width: '72px', flexShrink: 0, aspectRatio: '2/3', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(61,42,30,0.28)' }}>
                <img src={coverImage} alt="" aria-hidden crossOrigin="anonymous" style={{ position: 'absolute', inset: '-10%', width: '120%', height: '120%', objectFit: 'cover', filter: 'blur(8px) brightness(0.4)' }} />
                <img src={coverImage} alt={targetTitle} crossOrigin="anonymous" style={{ position: 'relative', width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
              </div>
            )}

            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: '0.72rem', color: C.driftwood, margin: '0 0 0.2rem' }}>
                I wanted to buy
              </p>
              <h1 style={{ fontFamily: 'Lora, serif', fontStyle: 'italic', fontWeight: 700, fontSize: 'clamp(1.05rem, 4vw, 1.35rem)', lineHeight: 1.2, color: C.darkRoast, margin: '0 0 0.4rem' }}>
                {targetTitle}
              </h1>
              {intentWhy && (
                <p style={{
                  fontFamily: 'Lora, serif', fontStyle: 'italic',
                  fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)', color: C.bark,
                  lineHeight: 1.4, margin: '0 0 0.4rem',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  because {intentWhy.replace(/^["""'']+|["""'']+$/g, '').replace(/[.!?]+$/, '')}
                </p>
              )}
              {recs.length > 0 && (() => {
                const count = Math.min(recs.length, 4)
                return (
                  <p style={{ fontSize: '0.78rem', color: C.bark, lineHeight: 1.4, margin: 0 }}>
                    but I already had <strong>{count}</strong> {count === 1 ? 'game' : 'games'} that scratch the same itch
                  </p>
                )
              })()}
            </div>
          </div>
        </div>

        {/* Grid */}
        <div style={{ padding: '0 1.25rem 1rem' }}>
          {recs.length > 0 ? (() => {
            const display = recs.slice(0, 4)
            const cols = display.length === 3 ? 3 : 2
            return (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '0.85rem' }}>
                {display.map((rec, idx) => <GameCard key={idx} rec={rec} idx={idx} />)}
              </div>
            )
          })() : (
            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: C.bark, padding: '1rem 0' }}>
              Backlog Buddy found no matches for this one.
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '0.75rem 1.25rem 1.25rem', textAlign: 'center' }}>
          <p style={{ fontFamily: 'Lora, serif', fontStyle: 'italic', fontSize: '0.72rem', color: C.bark, margin: '0 0 0.2rem', textAlign: 'center' }}>
            Found these hiding in my backlog.
          </p>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.68rem', fontWeight: 700, color: C.terracotta, margin: 0, textAlign: 'center' }}>
            backlogexplorer.com
          </p>
        </div>
      </div>

      {/* ── Buttons below card ── */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
        <button onClick={() => setDrawerOpen(true)} style={{ background: C.terracotta, color: '#fff', border: 'none', borderRadius: '999px', padding: '0.75rem 1.75rem', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Nunito, sans-serif', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          Share these picks
        </button>
        <Link to="/auth?mode=signup" style={{ color: 'rgba(253,248,242,0.7)', fontSize: '0.88rem', fontWeight: 700, textDecoration: 'none' }}>
          Try it with your library
        </Link>
        <Link to="/" style={{ color: 'rgba(253,248,242,0.4)', fontSize: '0.78rem', fontWeight: 500, textDecoration: 'none', marginTop: '0.25rem' }}>
          Find what to play tonight ✦
        </Link>
      </div>

      <ShareDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} url={shareUrl} title={shareTitle} cardRef={cardRef} />
    </div>
  )
}

export default SharePage
